import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { 
  Leaf, ArrowLeft, Search, Clock, Pill, Info,
  Moon, Zap, Shield, Brain, Heart, Scale, Utensils, 
  Bone, Sparkles, Smile, Loader2, ChevronDown, ChevronUp,
  Monitor, Dumbbell, Coffee, Plane, Briefcase, Apple, Timer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  brain_focus: Brain,
  digestion: Utensils,
  energy: Zap,
  fertility: Shield,
  hair_skin: Sparkles,
  heart: Heart,
  immunity: Shield,
  joints: Bone,
  menopause: Shield,
  mood: Smile,
  sleep: Moon,
  stress: Brain,
  weight: Scale
};

const categoryColors = {
  brain_focus: 'bg-violet-100 text-violet-600 border-violet-200',
  digestion: 'bg-orange-100 text-orange-600 border-orange-200',
  energy: 'bg-amber-100 text-amber-600 border-amber-200',
  fertility: 'bg-pink-100 text-pink-600 border-pink-200',
  hair_skin: 'bg-pink-100 text-pink-600 border-pink-200',
  heart: 'bg-red-100 text-red-600 border-red-200',
  immunity: 'bg-green-100 text-green-600 border-green-200',
  joints: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  menopause: 'bg-purple-100 text-purple-600 border-purple-200',
  mood: 'bg-yellow-100 text-yellow-600 border-yellow-200',
  sleep: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  stress: 'bg-purple-100 text-purple-600 border-purple-200',
  weight: 'bg-rose-100 text-rose-600 border-rose-200'
};

const categoryLabels = {
  brain_focus: 'Brain & Focus',
  digestion: 'Digestion',
  energy: 'Energy',
  fertility: 'Fertility',
  hair_skin: 'Hair & Skin',
  heart: 'Heart Health',
  immunity: 'Immunity',
  joints: 'Joint Health',
  menopause: 'Menopause',
  mood: 'Mood Support',
  sleep: 'Sleep',
  stress: 'Stress Relief',
  weight: 'Weight Management'
};

const priorityColors = {
  essential: 'bg-primary text-white',
  recommended: 'bg-secondary/20 text-secondary',
  optional: 'bg-muted text-muted-foreground'
};

// Lifestyle needs with curated supplement lists (not broad keywords)
const lifestyleNeeds = [
  { 
    id: 'shift_work', 
    label: 'Shift Work / Burnout', 
    icon: Coffee,
    supplements: ['Melatonin', 'Ashwagandha', 'Magnesium Glycinate', 'B-Complex', 'Rhodiola Rosea', 'L-Theanine'],
    color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
  },
  { 
    id: 'processed_diet', 
    label: 'Highly Processed Diet', 
    icon: Apple,
    supplements: ['Probiotic (Multi-strain)', 'Omega-3 Fish Oil (EPA/DHA)', 'B-Complex', 'Magnesium Glycinate', 'Zinc', 'Psyllium Husk', 'Vitamin D3'],
    color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
  },
  { 
    id: 'digital_strain', 
    label: 'Digital Eye Strain', 
    icon: Monitor,
    supplements: ['Lutein & Zeaxanthin', 'Bilberry Extract', 'Omega-3 Fish Oil (DHA-rich)', 'Vitamin A (Beta-Carotene)', 'Astaxanthin', 'Zinc'],
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
  },
  { 
    id: 'athletic', 
    label: 'Athletic Recovery', 
    icon: Dumbbell,
    supplements: ['Creatine Monohydrate', 'Magnesium Glycinate', 'Collagen Peptides', 'Omega-3 Fish Oil (EPA/DHA)', 'CoQ10 (Ubiquinol)', 'L-Glutamine'],
    color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
  },
  { 
    id: 'desk_job', 
    label: 'Desk Job / Sedentary', 
    icon: Briefcase,
    supplements: ['Vitamin D3', 'Magnesium Glycinate', 'Omega-3 Fish Oil (EPA/DHA)', 'Turmeric/Curcumin', 'CoQ10 (Ubiquinol)'],
    color: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
  },
  { 
    id: 'travel', 
    label: 'Frequent Travel / Jet Lag', 
    icon: Plane,
    supplements: ['Melatonin', 'Vitamin C', 'Zinc', 'Probiotic (Multi-strain)', 'Ashwagandha', 'Elderberry'],
    color: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100'
  },
  { 
    id: 'high_stress', 
    label: 'High Stress Profession', 
    icon: Timer,
    supplements: ['Ashwagandha', 'L-Theanine', 'Magnesium Glycinate', 'Rhodiola Rosea', 'B-Complex', 'GABA (Gamma-Aminobutyric Acid)'],
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
  },
  { 
    id: 'aging', 
    label: 'Aging & Longevity', 
    icon: Heart,
    supplements: ['CoQ10 (Ubiquinol)', 'Omega-3 Fish Oil (EPA/DHA)', 'Collagen Peptides', 'Vitamin D3', 'Vitamin C', 'Zinc'],
    color: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
  }
];

export default function SupplementsPage() {
  const navigate = useNavigate();
  const [supplements, setSupplements] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLifestyle, setSelectedLifestyle] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const lifestyleSectionRef = useRef(null);

  useEffect(() => {
    const fetchSupplements = async () => {
      try {
        const response = await axios.get(`${API}/supplements`);
        setSupplements(response.data.supplements);
      } catch (error) {
        console.error('Error fetching supplements:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSupplements();
  }, []);

  const allSupplements = Object.values(supplements).flat();
  
  // Helper to normalize benefits to array
  const getBenefitsArray = (benefits) => {
    if (Array.isArray(benefits)) return benefits;
    if (typeof benefits === 'string') return benefits.split(';').map(b => b.trim()).filter(b => b);
    return [];
  };
  
  const filteredSupplements = allSupplements.filter(supp => {
    const benefitsArray = getBenefitsArray(supp.benefits);
    const searchLower = searchQuery.toLowerCase();
    const nameLower = supp.name.toLowerCase();
    
    // Check if matches search query
    const matchesSearch = 
      nameLower.includes(searchLower) ||
      benefitsArray.some(b => b.toLowerCase().includes(searchLower));
    
    // Check if matches category filter
    const matchesCategory = selectedCategory === 'all' || supp.category === selectedCategory;
    
    // Check if matches lifestyle filter
    let matchesLifestyle = true;
    if (selectedLifestyle) {
      const lifestyle = lifestyleNeeds.find(l => l.id === selectedLifestyle);
      if (lifestyle) {
        // Match by curated supplement name list
        matchesLifestyle = lifestyle.supplements.some(suppName => 
          supp.name.toLowerCase() === suppName.toLowerCase()
        );
      }
    }
    
    return matchesSearch && matchesCategory && matchesLifestyle;
  }).sort((a, b) => {
    const priorityOrder = { essential: 0, recommended: 1, optional: 2 };
    return (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
  }).filter((supp, index, arr) => {
    // Deduplicate by name — keep the first (highest priority) occurrence
    return arr.findIndex(s => s.name === supp.name) === index;
  });

  const categories = Object.keys(supplements).sort((a, b) => {
    const labelA = categoryLabels[a] || a;
    const labelB = categoryLabels[b] || b;
    return labelA.localeCompare(labelB);
  });

  const toggleExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const scrollToResults = () => {
    setTimeout(() => {
      lifestyleSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A]"
          >
            <Logo size="default" linkTo={null} className="pointer-events-none" />
          </button>
          <Button
            onClick={() => navigate('/quiz')}
            className="btn-primary"
          >
            Get Personalized Plan
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Link 
            to="/dashboard"
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">
            Supplement Guide
          </h1>
          <p className="text-muted-foreground max-w-2xl text-sm">
            An independent, evidence-based database. Understand the clinical role of each nutrient, optimal dosages, and how to identify high-quality formulations.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search supplements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-white border-border"
            />
          </div>
        </motion.div>

        {/* Category Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">By Health Category</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setSelectedCategory('all'); setSelectedLifestyle(null); scrollToResults(); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all' && !selectedLifestyle
                  ? 'bg-primary text-white'
                  : 'bg-white border border-border hover:border-primary/30'
              }`}
            >
              All
            </button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat] || Shield;
              const isSelected = selectedCategory === cat && !selectedLifestyle;
              return (
                <button
                  key={cat}
                  onClick={() => { setSelectedCategory(cat); setSelectedLifestyle(null); scrollToResults(); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                    isSelected
                      ? 'bg-primary text-white'
                      : 'bg-white border border-border hover:border-primary/30'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {categoryLabels[cat]}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Lifestyle Needs Pills */}
        <motion.div
          ref={lifestyleSectionRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
          style={{ scrollMarginTop: '100px' }}
        >
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">By Lifestyle Need</p>
          <div className="flex flex-wrap gap-2">
            {lifestyleNeeds.map(lifestyle => {
              const Icon = lifestyle.icon;
              const isSelected = selectedLifestyle === lifestyle.id;
              return (
                <button
                  key={lifestyle.id}
                  onClick={() => { 
                    setSelectedLifestyle(isSelected ? null : lifestyle.id); 
                    setSelectedCategory('all');
                    scrollToResults();
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 border ${
                    isSelected
                      ? 'bg-primary text-white border-primary'
                      : lifestyle.color
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {lifestyle.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">
          Showing {filteredSupplements.length} supplements
          {selectedLifestyle && (
            <span className="ml-1">
              for <span className="font-medium text-[#1A1A1A]">{lifestyleNeeds.find(l => l.id === selectedLifestyle)?.label}</span>
            </span>
          )}
          {selectedCategory !== 'all' && !selectedLifestyle && (
            <span className="ml-1">
              in <span className="font-medium text-[#1A1A1A]">{categoryLabels[selectedCategory]}</span>
            </span>
          )}
        </p>

        {/* Supplements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSupplements.map((supplement, index) => {
            const Icon = categoryIcons[supplement.category] || Shield;
            const color = categoryColors[supplement.category] || 'bg-gray-100 text-gray-600 border-gray-200';
            const isExpanded = expandedCards[index];
            
            return (
              <motion.div
                key={`${supplement.name}-${index}`}
                data-testid={`supplement-card-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.03 }}
                className="bg-white rounded-xl border border-border/50 overflow-hidden hover:border-primary/30 transition-all hover:shadow-[0_8px_24px_rgba(74,108,88,0.08)]"
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${color.split(' ')[0]} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1A1A1A]">{supplement.name}</h3>
                        <span className="text-xs text-muted-foreground capitalize">{supplement.category.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${priorityColors[supplement.priority]}`}>
                      {supplement.priority}
                    </span>
                  </div>

                  {/* Quick Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Pill className="w-4 h-4 text-primary" />
                      <span className="font-medium text-primary">{supplement.dosage}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{supplement.timing}</span>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {getBenefitsArray(supplement.benefits).slice(0, 2).map((benefit, i) => (
                      <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full">
                        {benefit}
                      </span>
                    ))}
                  </div>

                  {/* Expand Button */}
                  <button
                    onClick={() => toggleExpand(index)}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <span>Show less</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span>View details</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="pt-4 mt-4 border-t border-border space-y-4"
                    >
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                          How to Take
                        </h4>
                        <p className="text-sm text-[#1A1A1A]">{supplement.how_to_take}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                          Duration
                        </h4>
                        <p className="text-sm text-[#1A1A1A]">{supplement.duration}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          What to Look For
                        </h4>
                        <p className="text-sm text-[#1A1A1A]">{supplement.what_to_look_for}</p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredSupplements.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No supplements found matching your criteria.</p>
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center p-12 rounded-2xl bg-primary/5 border border-primary/10"
        >
          <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
            Not sure which supplements are right for you?
          </h3>
          <p className="text-muted-foreground mb-6">
            Take our personalized health assessment to get AI-powered recommendations tailored to your needs.
          </p>
          <Button
            onClick={() => navigate('/quiz')}
            className="btn-primary"
          >
            Take Free Assessment
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
